import { z } from 'zod';

import { type WorkspaceRole } from './enums.js';
import { localeSchema, type Locale } from './locales.js';

/// Kimlik akışının FE/BE ortak sözleşmesi.
///
/// Doğrulama kuralları TEK yerde: form da bu şemayı çalıştırır, controller da.
/// İki tarafta ayrı yazılsaydı "frontend geçirdi, backend reddetti" farkı
/// kaçınılmazdı — kullanıcı formu doldurup gönderdikten sonra hata görürdü.

const email = z
  .email('Geçerli bir e-posta adresi girin')
  .max(254) // RFC 5321
  .transform((v) => v.trim().toLowerCase());

/**
 * PAROLASIZ GİRİŞ — e-postaya kod + bağlantı (10 Eki 2026, kullanıcı kararı)
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Akış
 * ═══════════════════════════════════════════════════════════════════════════
 * Parola YOK — ne kayıtta, ne girişte, ne ayarlarda. Kişi adresini yazıyor,
 * adrese tek bir posta gidiyor; postada altı haneli bir KOD ve bir giriş
 * BAĞLANTISI var (Claude / ChatGPT'deki gibi). Hangisi önce kullanılırsa o
 * girişi tamamlıyor, ikisi birlikte ölüyor.
 *
 *   start  → `POST /auth/email/start`   adrese posta (yanıt adres kayıtlı mı
 *                                      SÖYLEMİYOR — her adrese aynı cevap)
 *   kod    → `POST /auth/email/verify`  formdaki altı hane
 *   bağ    → `POST /auth/email/link`    postadaki bağlantının açtığı sayfa
 *
 * Giriş ile kayıt AYNI akış: adresin hesabı yoksa doğrulama anında açılıyor.
 * Hesap BAŞLATMADA açılmıyor — yazım hatalı bir adres, kimsenin tamamlayamayacağı
 * bir doğrulamada kalıyor ve ortada sahipsiz hesap birikmiyor.
 *
 * Doğrulanmış posta kutusu = doğrulanmış e-posta: bu yoldan açılan ya da giren
 * her hesap `emailVerified` alıyor. Ayrı bir "adresini doğrula" turu yok.
 */
export const EMAIL_CODE = {
  /// Kodun hane sayısı. Sayı (harf değil): telefonda sayı tuş takımı açılıyor
  /// ve posta istemcileri altı haneli sayıyı "tek dokunuşla kopyala" yapıyor.
  length: 6,
  /// Kodun ve bağlantının ömrü (saniye).
  ttlSeconds: 15 * 60,
  /// Bir koda kaç yanlış deneme hakkı var — sonra kod ölüyor, yenisi isteniyor.
  /// 10⁶ olasılıkta beş deneme: kaba kuvvetin başarı şansı 1/200.000.
  maxAttempts: 5,
  /// Yeni kod istemeden önce beklenecek süre (aynı adrese posta yağmasın).
  resendSeconds: 30,
  /// Bir adrese bir saatte en fazla kaç posta — taciz kapısını kapatıyor.
  maxSendsPerHour: 5,
} as const;

/** Altı hane — boşluk ve tire kopyalarken gelebiliyor, sunucu ayıklıyor. */
const emailCode = z
  .string()
  .transform((v) => v.replace(/[\s-]/g, ''))
  .pipe(z.string().regex(/^\d{6}$/, 'Kod altı haneli olmalı'));

/**
 * Bağlantıyla girildiğinde dönülecek uygulama içi yol (`/dashboard/...`,
 * `/invite/...`). Yalnız `/` ile başlayan, `//` ile başlamayan yol — aksi hâlde
 * giriş bağlantısı başka bir siteye açılan kapı olurdu (open redirect).
 * Geçersiz değer reddedilmiyor, DÜŞÜRÜLÜYOR: formdaki adres çubuğundan gelen
 * bir değer yüzünden kişinin girişi başarısız olmamalı.
 */
const nextPath = z
  .string()
  .max(500)
  .refine((v) => v.startsWith('/') && !v.startsWith('//') && !v.includes('\\'))
  .optional()
  .catch(undefined);

export const emailSignInStartSchema = z.object({
  email,
  /// Hesap YOKSA açılacak hesabın adı. Yalnız kayıt ekranı gönderiyor; hesap
  /// varsa yok sayılıyor (giriş, mevcut adı ezmez).
  name: z.string().trim().min(2, 'Adınızı girin').max(120).optional(),
  /// Postanın dili — hesap yoksa açılacak hesabın dili de bu. Hesap varsa
  /// posta hesabın kendi dilinde gidiyor.
  locale: localeSchema.default('en'),
  next: nextPath,
});
export type EmailSignInStartInput = z.infer<typeof emailSignInStartSchema>;

export interface EmailSignInStartResponse {
  /// Posta gerçekten çıktı mı (SMTP). `false` → arayüz "gönderilemedi, tekrar
  /// dene" diyor; kod beklenen bir postayı boşuna aratmıyor. Adres kayıtlı
  /// olsun olmasın posta gidiyor, yani bu alan kimin kayıtlı olduğunu
  /// söylemiyor.
  sent: boolean;
  /// Kod ve bağlantı kaç saniye geçerli.
  expiresInSeconds: number;
  /// "Kodu tekrar gönder" kaç saniye sonra açılıyor.
  resendInSeconds: number;
}

export const emailSignInCodeSchema = z.object({ email, code: emailCode });
export type EmailSignInCodeInput = z.infer<typeof emailSignInCodeSchema>;

/**
 * Postadaki bağlantının taşıdığı iki parça. Bağlantı `…/auth/link#<jeton>.<adres>`
 * biçiminde — ikisi de adresin `#` sonrasında, yani sunucuya, nginx loguna ve
 * yönlendirme başlığına (`Referer`) HİÇ gitmiyor; sayfadaki betik okuyup
 * gövdeyle gönderiyor.
 */
export const emailSignInLinkSchema = z.object({
  email,
  token: z.string().min(20, 'Bağlantı geçersiz').max(200),
});
export type EmailSignInLinkInput = z.infer<typeof emailSignInLinkSchema>;

export const verifyEmailSchema = z.object({
  token: z.string().min(20, 'Bağlantı geçersiz'),
});
export type VerifyEmailInput = z.infer<typeof verifyEmailSchema>;

export const resendVerificationSchema = z.object({ email });
export type ResendVerificationInput = z.infer<typeof resendVerificationSchema>;

// ─────────────────────────────── Yanıtlar ───────────────────────────────

/// Oturumdaki kullanıcının arayüze dönen hâli. Yanıt tipi Prisma satırından
/// ayrı tutuluyor: tabloya eklenen her kolon kendiliğinden arayüze sızmasın.
export interface SessionUser {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  locale: Locale;
  emailVerified: boolean;
  isPlatformAdmin: boolean;
}

/// Kullanıcının üyesi olduğu workspace'in özeti — switcher bunu listeler.
export interface SessionWorkspace {
  id: string;
  name: string;
  slug: string;
  role: WorkspaceRole;
}

export interface SessionResponse {
  user: SessionUser;
  workspaces: SessionWorkspace[];
  /// Etkin workspace kimliği; çerezden okunur, yoksa ilk üyelik.
  activeWorkspaceId: string | null;
}

/// Kod ya da bağlantıyla tamamlanan girişin yanıtı.
export interface EmailSignInResponse extends SessionResponse {
  /// Bu girişte YENİ hesap açıldı mı — arayüz kişiyi kurulum sihirbazına
  /// götürüyor (yeni alan plansız, kabuk zaten oraya yollardı; doğrudan gitmek
  /// bir yönlendirme turunu atlıyor).
  created: boolean;
  /// Başlatırken verilen dönüş yolu. Bağlantı BAŞKA bir cihazda açılabildiği
  /// için yol tarayıcıda değil sunucuda saklanıyor ve burada geri geliyor.
  next: string | null;
}

/// Hata gövdesi. Arayüz `code` üzerinden dallanır, `message` yalnızca
/// gösterim içindir — metin değişince davranış bozulmasın.
export const AuthErrorCode = {
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  /// Kod yanlış. `details.attemptsLeft` kalan hakkı söylüyor; 0 ise kod öldü.
  CODE_INVALID: 'CODE_INVALID',
  /// Etkin kod yok: süresi doldu, kullanıldı ya da yenisi istendi.
  CODE_EXPIRED: 'CODE_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  RATE_LIMITED: 'RATE_LIMITED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
} as const;
export type AuthErrorCode = (typeof AuthErrorCode)[keyof typeof AuthErrorCode];

/**
 * "Google ile giriş" geri dönüşünün hata kodları — 23 Eyl 2026.
 *
 * Akış tarayıcı yönlendirmesi olduğu için hata JSON gövdesiyle değil,
 * giriş sayfasının adresinde dönüyor: `/login?error=google_<kod>`. Giriş
 * formu bu listeyi okuyup kendi dilinde bir satır basıyor; listede olmayan
 * değer `failed` sayılıyor (adres çubuğuna elle yazılan şey metin üretmesin).
 */
export const GOOGLE_LOGIN_ERROR = {
  /** Kullanıcı Google ekranında "İptal"e bastı — hata değil, sessiz dönüş. */
  CANCELLED: 'cancelled',
  /** `state` yok/süresi doldu/başka tarayıcıya ait — akış baştan başlamalı. */
  EXPIRED: 'expired',
  /** Google e-postayı doğrulanmış saymıyor; bu adresle hesap açılamaz. */
  EMAIL_UNVERIFIED: 'email_unverified',
  /** Sunucuda istemci kimliği yok. */
  UNAVAILABLE: 'unavailable',
  /** Kod takası ya da kimlik okuması başarısız. */
  FAILED: 'failed',
} as const;
export type GoogleLoginError = (typeof GOOGLE_LOGIN_ERROR)[keyof typeof GOOGLE_LOGIN_ERROR];
export const GOOGLE_LOGIN_ERROR_PARAM_PREFIX = 'google_';

/**
 * BAŞARISIZ GİRİŞ UYARISININ EŞİĞİ — 16 Eyl 2026.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Neden her denemede değil
 * ═══════════════════════════════════════════════════════════════════════════
 * "Yanlış kod denendi" bildirimini HER denemede göndermek iki şeyi birden
 * bozar:
 *
 *   • GÜRÜLTÜ: kodu iki kez yanlış yazan herkes bildirim alır ve bildirim
 *     zili anlamını kaybeder.
 *   • TACİZ KAPISI: adresi bilen biri, art arda yanlış kod yazarak istediği
 *     kişiye istediği kadar e-posta/SMS gönderTEBİLİR — ve SMS ücretli. Eşik,
 *     saldırıya harcanacak bedeli bize değil saldırgana yükleyen tek şey.
 *
 * Pencere KAYAN değil sabit: ilk başarısız denemede sayaç açılıyor ve 15
 * dakika sonra kendiliğinden düşüyor. Başarılı giriş sayacı SIFIRLIYOR —
 * yoksa kodu üç kez yanlış yazıp dördüncüde giren kullanıcı, ertesi denemede
 * iki hatayla uyarı alırdı.
 *
 * Eşik bir kodun deneme hakkıyla (`EMAIL_CODE.maxAttempts`) aynı: tek bir kodu
 * sonuna kadar yanlış denemek, kişinin kendisi değilse tam da haber
 * verilmesi gereken an.
 */
export const AUTH_FAILURE = {
  /// Kaçıncı denemede bildirim çıkıyor.
  threshold: 5,
  /// Sayacın ömrü (saniye).
  windowSeconds: 15 * 60,
} as const;
